(() => {
  if (window.__lounelleNativeCartEnhancementsV6) return;
  window.__lounelleNativeCartEnhancementsV6 = true;

  const TIMER_DURATION = 10 * 60 * 1000;
  const TIMER_STORAGE_KEY = 'lounelle_cart_urgency_10m_deadline_v2';

  const getRoot = () => document.querySelector('[data-lounelle-native-cart]');
  const rootPath = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartAddUrl = () => `${rootPath()}cart/add.js`;
  const cartChangeUrl = () => `${rootPath()}cart/change.js`;

  let orphanInsuranceCleanupInProgress = false;

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart update failed.');
    }

    return data;
  }

  function requestNativeCartRefresh() {
    document.dispatchEvent(
      new CustomEvent('theme:cart:refresh', { bubbles: true })
    );
  }

  function fillTemplate(template, values = {}) {
    let output = String(template || '');

    Object.entries(values).forEach(([key, value]) => {
      output = output.split(`[${key}]`).join(String(value));
    });

    return output;
  }

  function readSessionNumber(key) {
    try {
      const value = Number(sessionStorage.getItem(key) || 0);
      return Number.isFinite(value) && value > 0 ? value : 0;
    } catch (_) {
      return 0;
    }
  }

  function writeSessionValue(key, value) {
    try {
      if (
        value === null ||
        value === undefined ||
        value === false ||
        value === 0 ||
        value === ''
      ) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, String(value));
      }
    } catch (_) {}
  }

  function formatTime(milliseconds) {
    const total = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  function getState(root) {
    const state = root?.querySelector(
      '[data-cart-price-holder] [data-lounelle-cart-state]'
    );

    if (!state) return null;

    return {
      merchandiseCount: Math.max(0, Number(state.dataset.merchandiseCount || 0)),
      promoEligibleCount: Math.max(0, Number(state.dataset.promoEligibleCount || 0)),
      promoEligibleSubtotal: Math.max(0, Number(state.dataset.promoEligibleSubtotal || 0)),
      tierOneQty: Math.max(1, Number(state.dataset.tierOneQty || 2)),
      tierTwoQty: Math.max(2, Number(state.dataset.tierTwoQty || 4)),
      tierOnePercent: Math.max(0, Number(state.dataset.tierOnePercent || 15)),
      tierTwoPercent: Math.max(0, Number(state.dataset.tierTwoPercent || 20)),
      nativeTotal: Math.max(0, Number(state.dataset.nativeTotal || 0)),
      insuranceActive: state.dataset.insuranceActive === 'true',
      insuranceLineKey: state.dataset.insuranceLineKey || ''
    };
  }

  function updateTimer(root, state) {
    const holder = root.querySelector('[data-lounelle-cart-timer]');
    const text = root.querySelector('[data-lounelle-cart-timer-text]');

    if (!holder || !text) return;

    const hasItems = state.merchandiseCount > 0;
    holder.classList.toggle('hidden', !hasItems);

    if (!hasItems) {
      writeSessionValue(TIMER_STORAGE_KEY, null);
      return;
    }

    let deadline = readSessionNumber(TIMER_STORAGE_KEY);

    if (!deadline) {
      deadline = Date.now() + TIMER_DURATION;
      writeSessionValue(TIMER_STORAGE_KEY, deadline);
    }

    const remaining = deadline - Date.now();

    if (remaining <= 0) {
      text.textContent = holder.dataset.timerExpiredMessage || '';
      return;
    }

    text.textContent = fillTemplate(holder.dataset.timerTemplate, {
      TIME: formatTime(remaining)
    });
  }

  function updateBenefits(root, state) {
    const holder = root.querySelector('[data-lounelle-cart-benefits]');
    if (!holder) return;

    const qty = state.promoEligibleCount;
    const tierOne = Math.max(1, state.tierOneQty || Number(holder.dataset.tierOneQty || 2));
    const tierTwo = Math.max(
      tierOne + 1,
      state.tierTwoQty || Number(holder.dataset.tierTwoQty || 4)
    );

    let progress = 0;

    if (qty > 0 && qty < tierOne) {
      progress = 0.5 * (qty / tierOne);
    } else if (qty >= tierOne && qty < tierTwo) {
      progress =
        0.5 + 0.5 * ((qty - tierOne) / (tierTwo - tierOne));
    } else if (qty >= tierTwo) {
      progress = 1;
    }

    holder.style.setProperty(
      '--lounelle-progress',
      String(Math.max(0, Math.min(1, progress)))
    );

    const milestones = Array.from(
      holder.querySelectorAll('[data-benefit-threshold]')
    );

    let currentAssigned = false;

    milestones.forEach((node) => {
      const threshold = Math.max(
        1,
        Number(node.dataset.benefitThreshold || 1)
      );
      const unlocked = qty >= threshold;

      node.classList.toggle('is-unlocked', unlocked);
      node.classList.remove('is-current');

      if (!unlocked && !currentAssigned) {
        node.classList.add('is-current');
        currentAssigned = true;
      }
    });

    const message = holder.querySelector('[data-lounelle-benefits-message]');
    if (!message) return;

    if (qty <= 0) {
      message.textContent = '';
    } else if (qty < tierOne) {
      const left = tierOne - qty;
      message.textContent = fillTemplate(
        left === 1 ? holder.dataset.messageOne : holder.dataset.messageMany,
        {
          COUNT: left,
          DISCOUNT: holder.dataset.tierOneLabel || '-15%'
        }
      );
    } else if (qty < tierTwo) {
      const left = tierTwo - qty;
      message.textContent = fillTemplate(
        left === 1 ? holder.dataset.messageOne : holder.dataset.messageMany,
        {
          COUNT: left,
          DISCOUNT: holder.dataset.tierTwoLabel || '-20%'
        }
      );
    } else {
      message.textContent = holder.dataset.messageAll || '';
    }
  }

  function updateShippingCopy(root) {
    const source = root.querySelector('.lounelle-cart__reassurance-item span');
    const target = root.querySelector('.lounelle-cart__native-cart-text');
    const freeLabel = source?.textContent?.trim();

    if (target && freeLabel) {
      target.textContent = freeLabel;
    }
  }

  function mixProductId(productId) {
    const value = String(productId || '');
    let hash = 2166136261;

    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x85ebca6b);
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 0xc2b2ae35);
    hash ^= hash >>> 16;

    return hash >>> 0;
  }

  function getObservedPrice(productId, basePrice, realComparePrice) {
    if (realComparePrice > basePrice) return realComparePrice;

    const minDiscount = 20;
    const maxDiscount = basePrice > 5500 ? 40 : 50;
    const discountPercent =
      minDiscount +
      (mixProductId(productId) % (maxDiscount - minDiscount + 1));

    const rawComparePrice = (basePrice * 100) / (100 - discountPercent);
    const comparePrice =
      Math.round((rawComparePrice - 90) / 100) * 100 + 90;

    return Number.isFinite(comparePrice) && comparePrice > basePrice
      ? comparePrice
      : 0;
  }

  function formatThemeMoney(cents) {
    if (!window.theme || typeof window.theme.formatMoney !== 'function') {
      return '';
    }

    const withCurrency = Boolean(
      window.theme.settings && window.theme.settings.currency_code_enable
    );
    const format = withCurrency
      ? window.theme.moneyWithCurrencyFormat
      : window.theme.moneyFormat;

    return format ? window.theme.formatMoney(cents, format) : '';
  }

  function ensureObservedPriceNode(priceNode) {
    const compareNodes = Array.from(priceNode.querySelectorAll('del'));
    let observedNode =
      priceNode.querySelector('[data-lounelle-observed-price]') ||
      compareNodes.shift();

    compareNodes.forEach((node) => {
      if (node !== observedNode) node.remove();
    });

    if (!observedNode) {
      observedNode = document.createElement('del');
    }

    observedNode.setAttribute('data-lounelle-observed-price', 'true');
    priceNode.insertBefore(observedNode, priceNode.firstChild);

    return observedNode;
  }

  function updateObservedPricing(root) {
    const state = root.querySelector(
      '[data-cart-price-holder] [data-lounelle-cart-state]'
    );
    if (!state) return;

    const pricingByKey = new Map(
      Array.from(state.querySelectorAll('[data-lounelle-pricing-line]')).map(
        (node) => [node.dataset.key || '', node]
      )
    );

    let promoProductTotal = 0;

    root.querySelectorAll('[data-cart-item]').forEach((cartItem) => {
      const pricing = pricingByKey.get(cartItem.dataset.item || '');
      const priceNode = cartItem.querySelector('.cart__item__price');

      if (!pricing || !priceNode) return;

      const basePrice = Number(pricing.dataset.originalPrice || 0);
      const realComparePrice = Number(pricing.dataset.compareAtPrice || 0);
      const quantity = Math.max(0, Number(pricing.dataset.quantity || 0));
      const productId = pricing.dataset.productId || '';

      if (!productId || !Number.isFinite(basePrice) || basePrice <= 0) return;

      const observedPrice = getObservedPrice(
        productId,
        basePrice,
        Number.isFinite(realComparePrice) ? realComparePrice : 0
      );

      if (!observedPrice || observedPrice <= basePrice) {
        priceNode
          .querySelector('[data-lounelle-observed-price]')
          ?.remove();
        priceNode.classList.remove('lounelle-cart__price--promo');
        return;
      }

      promoProductTotal += (observedPrice - basePrice) * quantity;

      const observedNode = ensureObservedPriceNode(priceNode);
      const formattedObservedPrice = formatThemeMoney(observedPrice);

      if (formattedObservedPrice) {
        observedNode.textContent = formattedObservedPrice;
      }

      observedNode.hidden = false;
      priceNode.classList.add('lounelle-cart__price--promo');
    });

    const originalTotal = Number(state.dataset.originalTotal || 0);
    const subtotalNode = root.querySelector('[data-lounelle-summary-subtotal]');
    const promoRow = root.querySelector('[data-lounelle-promo-product-row]');
    const promoAmountNode = root.querySelector(
      '[data-lounelle-promo-product-amount]'
    );

    if (subtotalNode && Number.isFinite(originalTotal)) {
      const formattedSubtotal = formatThemeMoney(
        originalTotal + promoProductTotal
      );

      if (formattedSubtotal) {
        subtotalNode.textContent = formattedSubtotal;
      }
    }

    if (promoRow && promoAmountNode) {
      const hasPromoProduct = promoProductTotal > 0;
      promoRow.hidden = !hasPromoProduct;

      if (hasPromoProduct) {
        const formattedPromo = formatThemeMoney(promoProductTotal);
        promoAmountNode.textContent = formattedPromo ? `-${formattedPromo}` : '';
      } else {
        promoAmountNode.textContent = '';
      }
    }
  }

  function getVisualTier(state) {
    if (!state) return { percent: 0, amount: 0 };

    let percent = 0;

    if (state.promoEligibleCount >= state.tierTwoQty) {
      percent = state.tierTwoPercent;
    } else if (state.promoEligibleCount >= state.tierOneQty) {
      percent = state.tierOnePercent;
    }

    const amount =
      percent > 0
        ? Math.round((state.promoEligibleSubtotal * percent) / 100)
        : 0;

    return {
      percent,
      amount: Math.max(0, Number.isFinite(amount) ? amount : 0)
    };
  }

  function updateVisualTierPromotion(root, state) {
    const row = root.querySelector('[data-lounelle-tier-promo-row]');
    const amountNode = root.querySelector('[data-lounelle-tier-promo-amount]');
    const totalNode = root.querySelector('[data-lounelle-summary-total]');

    const tier = getVisualTier(state);
    const hasTier = tier.amount > 0;

    if (row) {
      row.hidden = !hasTier;
      row.dataset.lounelleTierPercent = String(tier.percent || 0);
    }

    if (amountNode) {
      const formattedAmount = hasTier ? formatThemeMoney(tier.amount) : '';
      amountNode.textContent = formattedAmount ? `-${formattedAmount}` : '';
    }

    if (totalNode) {
      const visualTotal = Math.max(0, state.nativeTotal - tier.amount);
      const formattedTotal = formatThemeMoney(visualTotal);

      if (formattedTotal) {
        totalNode.textContent = formattedTotal;
      }
    }
  }

  function updateInsurance(root, state) {
    const row = root.querySelector('[data-lounelle-insurance]');
    const button = row?.querySelector('[data-lounelle-insurance-toggle]');

    if (!row || !button) return;

    const available = button.dataset.insuranceAvailable === 'true';
    const selected =
      state.merchandiseCount > 0 && state.insuranceActive;

    row.classList.toggle('hidden', state.merchandiseCount <= 0);
    button.classList.toggle('is-active', selected);
    button.setAttribute('aria-checked', selected ? 'true' : 'false');
    button.classList.remove('is-loading');

    button.disabled =
      state.merchandiseCount <= 0 ||
      (!available && !state.insuranceActive);

    root.dataset.lounelleInsuranceSelected = selected ? 'true' : 'false';
  }

  async function cleanupOrphanInsurance(state) {
    if (
      orphanInsuranceCleanupInProgress ||
      state.merchandiseCount > 0 ||
      !state.insuranceActive ||
      !state.insuranceLineKey
    ) {
      return;
    }

    orphanInsuranceCleanupInProgress = true;

    try {
      await cartRequest(cartChangeUrl(), {
        id: state.insuranceLineKey,
        quantity: 0
      });

      requestNativeCartRefresh();
    } catch (error) {
      console.warn(
        '[Lounelle cart] Orphan insurance cleanup failed:',
        error
      );
    } finally {
      orphanInsuranceCleanupInProgress = false;
    }
  }

  function sync() {
    const root = getRoot();
    if (!root) return;

    const state = getState(root);
    if (!state) return;

    updateTimer(root, state);
    updateBenefits(root, state);
    updateShippingCopy(root);
    updateObservedPricing(root);
    updateVisualTierPromotion(root, state);
    updateInsurance(root, state);

    if (state.merchandiseCount <= 0 && state.insuranceActive) {
      cleanupOrphanInsurance(state);
    }
  }

  async function handleInsuranceClick(event) {
    const button = event.target.closest('[data-lounelle-insurance-toggle]');

    if (!button || !event.currentTarget.contains(button)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const root = event.currentTarget;
    const state = getState(root);

    if (
      !state ||
      state.merchandiseCount <= 0 ||
      button.classList.contains('is-loading')
    ) {
      return;
    }

    const available = button.dataset.insuranceAvailable === 'true';

    if (!state.insuranceActive && !available) {
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    const status = root.querySelector('[data-lounelle-insurance-status]');
    if (status) status.textContent = '';

    try {
      if (state.insuranceActive) {
        if (!state.insuranceLineKey) {
          throw new Error('Insurance line key is missing.');
        }

        await cartRequest(cartChangeUrl(), {
          id: state.insuranceLineKey,
          quantity: 0
        });
      } else {
        const variantId = Number(button.dataset.insuranceVariantId || 0);

        if (!variantId) {
          throw new Error('Insurance variant is not configured.');
        }

        await cartRequest(cartAddUrl(), {
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _lounelle_insurance: 'true'
              }
            }
          ]
        });
      }

      requestNativeCartRefresh();
    } catch (error) {
      console.warn('[Lounelle cart] Insurance update failed:', error);

      updateInsurance(root, state);

      if (status) {
        status.textContent =
          root.dataset.insuranceUnavailable ||
          'Assurance momentanément indisponible.';
      }
    }
  }

  function bindInsuranceToggle() {
    const root = getRoot();

    if (!root || root.dataset.lounelleInsuranceBound === 'true') {
      return;
    }

    root.dataset.lounelleInsuranceBound = 'true';
    root.addEventListener('click', handleInsuranceClick);
  }

  [
    'theme:cart:loaded',
    'theme:cart:open',
    'theme:cart:added',
    'theme:cart:change',
    'theme:cart:update'
  ].forEach((name) => {
    document.addEventListener(name, () => window.requestAnimationFrame(sync));
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        bindInsuranceToggle();
        sync();
      },
      { once: true }
    );
  } else {
    bindInsuranceToggle();
    sync();
  }

  window.setInterval(() => {
    const root = getRoot();
    const state = root ? getState(root) : null;

    if (root && state) {
      updateTimer(root, state);
    }
  }, 1000);
})();